import { useCallback, useEffect, useRef, useState } from 'react';
import { loadGis, type CredentialResponse } from '../auth/gis';

/**
 * AccountDeleteView — public account deletion page at /account/delete.
 *
 * Replaces the former static site/account/delete/index.html. Uses Google
 * Identity Services to verify identity, then POSTs to /account/delete on
 * the Worker. This is a public page — no web session cookie required.
 *
 * Uses the account-delete Google Client ID (distinct from the web app
 * sign-in client ID) to match the former static page's behavior.
 */

const GOOGLE_CLIENT_ID =
  '369475896218-5op2v330vhcmk34cv80v8etrorffk7qb.apps.googleusercontent.com';
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'https://api.loaf.binodswain.dev';

type DeleteStatus =
  | { kind: 'idle' }
  | { kind: 'progress'; msg: string }
  | { kind: 'ok'; msg: string }
  | { kind: 'error'; msg: string };

export default function AccountDeleteView() {
  const gidRef = useRef<HTMLDivElement>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [status, setStatus] = useState<DeleteStatus>({ kind: 'idle' });
  const [gisError, setGisError] = useState(false);

  const handleDelete = useCallback(async () => {
    if (!idToken || !confirmed) return;
    setStatus({ kind: 'progress', msg: 'Deleting your account…' });
    try {
      const res = await fetch(`${API_BASE_URL}/account/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, confirm: true }),
      });
      const data = await res.json();
      if (res.status === 200 && data.ok) {
        setStatus({
          kind: 'ok',
          msg: data.alreadyDeleted
            ? 'No active account found — already deleted.'
            : 'Your account has been deleted.',
        });
      } else {
        setStatus({
          kind: 'error',
          msg: `Could not delete your account: ${data.error ?? 'unknown error'}`,
        });
      }
    } catch (e) {
      setStatus({
        kind: 'error',
        msg: `Network error: ${e instanceof Error ? e.message : String(e)}`,
      });
    }
  }, [idToken, confirmed]);

  useEffect(() => {
    let cancelled = false;
    loadGis()
      .then((gis) => {
        if (cancelled || !gidRef.current) return;
        gis.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp: CredentialResponse) => {
            setIdToken(resp.credential);
            setStatus({ kind: 'ok', msg: 'Identity verified. Confirm to delete.' });
          },
        });
        gis.renderButton(gidRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
        });
      })
      .catch(() => {
        if (!cancelled) setGisError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-xl px-gutter py-gutter">
      <h1 className="font-headline-lg text-headline-lg font-bold text-on-surface">
        Delete your Loaf account
      </h1>
      <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
        Loaf account deletion
      </p>

      <div className="mt-6 rounded-2xl border border-tertiary/30 bg-tertiary/10 p-6">
        <p className="font-title-md text-title-md font-semibold text-tertiary">
          This is permanent and cannot be undone.
        </p>
        <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
          Deleting your Loaf account permanently removes:
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5 font-body-sm text-body-sm text-on-surface-variant">
          <li>Your server-side user record and metering data</li>
          <li>Your cloud usage history and credit balances</li>
          <li>Your local activity events backup</li>
        </ul>
        <p className="mt-3 font-body-sm text-body-sm text-on-surface-variant">
          <strong>Google Play subscription:</strong> Deleting your Loaf account
          does <strong>not</strong> cancel an active Google Play subscription.
          To cancel a subscription, visit{' '}
          <a
            href="https://play.google.com/store/account/subscriptions"
            className="font-medium text-tertiary underline"
          >
            Google Play subscriptions
          </a>
          .
        </p>
      </div>

      {gisError && (
        <p className="mt-6 font-body-sm text-body-sm text-error">
          Could not load Google Sign-In. Please check your network connection
          and try again.
        </p>
      )}

      <div ref={gidRef} className="mt-6 min-h-[40px]" />

      {idToken && (
        <div className="mt-6">
          <p className="font-body-md text-body-md text-on-surface">
            Sign in with Google to verify your identity, then confirm deletion.
          </p>
          <label className="mt-3 flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary"
            />
            <span className="font-body-sm text-body-sm text-on-surface">
              I understand this is permanent and cannot be undone.
            </span>
          </label>
          <button
            type="button"
            disabled={!confirmed || status.kind === 'progress'}
            onClick={handleDelete}
            className="mt-4 rounded-xl bg-error px-5 py-2.5 font-label-lg text-label-lg font-semibold text-on-error transition-colors hover:bg-error/90 disabled:cursor-not-allowed disabled:bg-surface-container-highest disabled:text-on-surface-variant"
          >
            Delete my account
          </button>
        </div>
      )}

      {status.kind !== 'idle' && (
        <p
          className={[
            'mt-4 font-label-lg text-label-lg font-semibold',
            status.kind === 'ok' && 'text-secondary',
            status.kind === 'error' && 'text-error',
            status.kind === 'progress' && 'text-primary',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {status.msg}
        </p>
      )}
    </div>
  );
}
